import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "../../ui/checkbox";
import { cn } from "../../../lib/utils";
import { getServiceIcon } from "./iconMap";
import { getCategoryName } from "../utils/getCategoryName";
import { buildCategoryTree, RawCategory } from "../utils/buildCategoryTree";
import type { Service } from "../../../types";

interface CategoryCheckboxTreeProps {
  categories: RawCategory[];
  /** Selected LEAF keys. */
  value: string[];
  onChange: (leafKeys: string[]) => void;
}

/**
 * Grouped service-category checkboxes. Standalone leaves render as a single
 * checkbox; grouping headers render a tri-state parent checkbox plus indented
 * child checkboxes. Only leaf keys are ever stored in `value`.
 */
export function CategoryCheckboxTree({ categories, value, onChange }: CategoryCheckboxTreeProps) {
  const { t, i18n } = useTranslation();
  const nodes = useMemo(() => buildCategoryTree(categories), [categories]);

  const has = (key: string) => value.includes(key);
  const label = (c: Service | RawCategory) => getCategoryName(c as any, i18n.language, t);

  const toggleOne = (key: string) => {
    onChange(has(key) ? value.filter((k) => k !== key) : [...value, key]);
  };

  const toggleGroup = (childKeys: string[], allSelected: boolean) => {
    if (allSelected) {
      const remove = new Set(childKeys);
      onChange(value.filter((k) => !remove.has(k)));
    } else {
      onChange(Array.from(new Set([...value, ...childKeys])));
    }
  };

  const Row = ({ service, checked, onToggle, indented }: {
    service: Service;
    checked: boolean;
    onToggle: () => void;
    indented?: boolean;
  }) => {
    const Icon = getServiceIcon(service.icon);
    return (
      <label
        className={cn(
          "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
          checked ? "border-primary bg-primary/10" : "border-border hover:border-primary/50 hover:bg-muted/50",
          indented && "ml-6"
        )}>
        <Checkbox checked={checked} onCheckedChange={onToggle} />
        <Icon className={cn("w-5 h-5", checked ? "text-primary" : "text-muted-foreground")} />
        <span className={cn("font-medium", checked ? "text-primary" : "text-foreground")}>
          {label(service)}
        </span>
      </label>
    );
  };

  return (
    <div className="space-y-2">
      {nodes.map((node) => {
        if (!node.isGroup) {
          return (
            <Row
              key={node.category.id}
              service={node.category}
              checked={has(node.category.id)}
              onToggle={() => toggleOne(node.category.id)}
            />
          );
        }

        const childKeys = node.children.map((c) => c.id);
        const selectedCount = childKeys.filter(has).length;
        const allSelected = childKeys.length > 0 && selectedCount === childKeys.length;
        const someSelected = selectedCount > 0 && !allSelected;
        const GroupIcon = getServiceIcon(node.category.icon);

        return (
          <div key={node.category.id} className="space-y-2">
            <label className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30 cursor-pointer">
              <Checkbox
                checked={allSelected ? true : someSelected ? "indeterminate" : false}
                onCheckedChange={() => toggleGroup(childKeys, allSelected)}
                className="data-[state=indeterminate]:bg-primary/60 data-[state=indeterminate]:border-primary data-[state=indeterminate]:text-primary-foreground"
              />
              <GroupIcon className="w-5 h-5 text-muted-foreground" />
              <span className="font-semibold text-foreground">{label(node.category)}</span>
              <span className="ml-auto text-xs text-muted-foreground">
                {selectedCount}/{childKeys.length}
              </span>
            </label>
            {node.children.map((child) => (
              <Row
                key={child.id}
                service={child}
                checked={has(child.id)}
                onToggle={() => toggleOne(child.id)}
                indented
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
